import React from 'react';
import {
    AppstoreOutlined,
    ClearOutlined,
    DownloadOutlined,
    ExportOutlined,
    ImportOutlined,
    MoonOutlined,
    PrinterOutlined,
    ShareAltOutlined,
    SunOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { MindMapContext } from '../../../Context/contex';
import { useDarkMode } from '../../DarkModeToggle';
import { useDownloadImage } from '../../DownloadImageButton';
import { usePrintMindMap } from '../../Print';
import { useClearMap } from '../../MapActions/useClearMap';
import { apps, openApp } from '../../AppLauncher';
import DocumentMembers from '../../Members/members';
import { getOtherMembers, isDocumentOwner } from '../documentAccess';
import MenuRow from './MenuRow';
import styles from './RightHeaderMenu.module.css';

const DIVIDER = { type: 'divider' };
const appKey = (app) => `app:${app.id}`;

const toolItems = [
    { key: 'download', label: <MenuRow icon={<DownloadOutlined />} label="Download image" /> },
    { key: 'import', label: <MenuRow icon={<ImportOutlined />} label="Import" /> },
    { key: 'export', label: <MenuRow icon={<ExportOutlined />} label="Export as .mindmap" /> },
    { key: 'print', label: <MenuRow icon={<PrinterOutlined />} label="Print" /> },
];

const clearItem = { key: 'clear', label: <MenuRow icon={<ClearOutlined />} label="Clear mind map" /> };

const appsItem = {
    key: 'apps',
    label: <MenuRow icon={<AppstoreOutlined />} label="Apps" />,
    popupClassName: styles.subPopup,
    children: apps.map((app) => ({
        key: appKey(app),
        label: <MenuRow icon={<img src={app.icon} alt="" className={styles.appIcon} />} label={app.name} />,
    })),
};

const appActions = Object.fromEntries(apps.map((app) => [appKey(app), () => openApp(app.url)]));

const membersItem = (count) => ({
    key: 'members',
    label: <MenuRow icon={<TeamOutlined />} label="Members" extra={<span className={styles.count}>{count}</span>} />,
    popupClassName: styles.subPopup,
    children: [{ key: 'members-list', label: <DocumentMembers />, disabled: true }],
});

const shareItem = { key: 'share', label: <MenuRow icon={<ShareAltOutlined />} label="Share" /> };

const modeItem = (isDark) => ({
    key: 'mode',
    label: <MenuRow icon={isDark ? <SunOutlined /> : <MoonOutlined />} label={isDark ? 'Light mode' : 'Dark mode'} />,
});

/**
 * The compact menu's rows, standing in for the tools and desktop actions below
 * 768px: map tools, then people, then theme and apps. Returns antd `menu`
 * props; a click runs the row's action and closes the menu.
 */
export const useMenuItems = ({ close, onImport, onExport, onShare }) => {
    const { documentInfo, userInfo, isTemplateDocument, isReadOnly } = MindMapContext();
    const downloadImage = useDownloadImage();
    const print = usePrintMindMap();
    const clearMap = useClearMap();
    const { dark, toggle: toggleDark } = useDarkMode();

    const memberCount = isTemplateDocument ? 0 : getOtherMembers(documentInfo, userInfo).length;
    const canShare = !isTemplateDocument && isDocumentOwner(documentInfo, userInfo);
    const peopleItems = [memberCount > 0 && membersItem(memberCount), canShare && shareItem].filter(Boolean);

    const items = [
        ...toolItems,
        ...(isReadOnly ? [] : [clearItem]),
        ...(peopleItems.length > 0 ? [DIVIDER, ...peopleItems] : []),
        DIVIDER,
        modeItem(dark),
        appsItem,
    ];

    const actions = {
        download: downloadImage,
        import: onImport,
        export: onExport,
        print,
        clear: clearMap,
        share: onShare,
        mode: toggleDark,
        ...appActions,
    };

    const onClick = ({ key }) => {
        close();
        actions[key]?.();
    };

    return { items, onClick };
};
